import { useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC, useContext } from 'react';
import { useDeepCompareEffect } from 'react-use';

import LoadingWrapper from '@ui/atoms/LoadingWrapper';

import { dataConstants as insuranceStatusConstants } from '@ui/routes/Membership/MembershipInsuranceStatusStep/MembershipInsuranceStatusStep.data';

import { IAuthenticatedMember } from '@hooks/useMemberInfo';

import { keysToCamel } from '@utils/butter';
import { doesNotExist } from '@utils/general';
import { combineMemberFragments } from '@utils/graphql';

import FlowContext from '@providers/FlowContextProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { ExemptionType, IWaivable } from '@data/insurance';

import { IFlowStepProps } from '@folx-types/flow-steps';

import { queryName, waivable, waivableName } from '@graphql/fragments/waivable';

import MembershipInsuranceCoverageStep, {
	IMembershipInsuranceCoverageStepPageContent,
} from './MembershipInsuranceCoverageStep.component';

interface IMembershipInsuranceCoverageStepPageProps extends IFlowStepProps {}

const MembershipInsuranceCoverageStepPage: FC<IMembershipInsuranceCoverageStepPageProps> = ({
	path,
	stepKey,
}) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "membership-insurance-coverage-step" }) {
				page_header
				page_info
				check_insurance_detail {
					title
					description
				}
				no_insurance_detail {
					title
					description
				}
				unsure_insurance_detail {
					title
					description
				}
				government_insurance_detail {
					title
					description
				}
			}
		}
	`);

	const { existingFormData, handleFormSubmission, handleNavBack } = useContext(FlowContext);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const {
		data: waivableData,
		loading: waivableLoading,
		error: waivableError,
	} = useQuery<IAuthenticatedMember<IWaivable>>(
		combineMemberFragments([waivableName], [waivable], queryName),
		{
			client: graphQLv2Client,
		}
	);

	useDeepCompareEffect(() => {
		if (
			doesNotExist(waivableData) ||
			doesNotExist(waivableData?.authenticatedMember?.isMembershipWaivable?.exemptBy) ||
			!waivableData?.authenticatedMember?.isMembershipWaivable?.isWaivable
		) {
			return;
		}

		const waivable = waivableData?.authenticatedMember?.isMembershipWaivable?.isWaivable;
		const exemption = waivableData?.authenticatedMember?.isMembershipWaivable?.exemptBy;
		const isExempt = exemption === ExemptionType.waiver || exemption === ExemptionType.employer;

		if (isExempt) {
			handleFormSubmission(
				{
					[insuranceStatusConstants.isWaivable]: waivable,
					[insuranceStatusConstants.exemptBy]: exemption,
				},
				stepKey
			);
		}
	}, [waivableData]);

	const refinedContent: IMembershipInsuranceCoverageStepPageContent = keysToCamel(
		pageContent
	) as IMembershipInsuranceCoverageStepPageContent;

	return (
		<LoadingWrapper loading={waivableLoading}>
			<MembershipInsuranceCoverageStep
				pageContent={refinedContent}
				path={path}
				stepKey={stepKey}
				existingFormData={existingFormData}
				handleFormSubmission={handleFormSubmission}
				handleNavBack={handleNavBack}
			/>
		</LoadingWrapper>
	);
};

export default MembershipInsuranceCoverageStepPage;
